const BIOMETRIC_KEY = 'coverly_biometric_device_v1';

export interface LocalBiometricUser {
  id: string;
  folio: string;
  nombre: string;
  nombreCompleto: string;
  email?: string;
  telefono?: string;
  [key: string]: any;
}

interface StoredBiometric {
  credentialId: string;
  memberId: string;
  user: LocalBiometricUser;
  createdAt: string;
}

const textEncoder = new TextEncoder();

function toBase64Url(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function randomChallenge() {
  const challenge = new Uint8Array(32);
  window.crypto.getRandomValues(challenge);
  return challenge;
}

export async function isBiometricAvailable() {
  if (!window.isSecureContext || !('PublicKeyCredential' in window) || !navigator.credentials) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export function getStoredBiometric() {
  try {
    const raw = localStorage.getItem(BIOMETRIC_KEY);
    return raw ? JSON.parse(raw) as StoredBiometric : null;
  } catch {
    return null;
  }
}

export function hasStoredBiometric() {
  return Boolean(getStoredBiometric()?.credentialId);
}

export async function registerBiometricCredential(user: LocalBiometricUser) {
  const available = await isBiometricAvailable();
  if (!available) {
    throw new Error('Este dispositivo no tiene biometria disponible o no permite passkeys en este navegador.');
  }

  const displayName = user.nombreCompleto || user.nombre || 'Cliente Coverly';
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: randomChallenge(),
      rp: {
        name: 'Coverly',
        id: window.location.hostname,
      },
      user: {
        id: textEncoder.encode(user.id.slice(0, 64)),
        name: user.email || user.telefono || user.folio,
        displayName,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'preferred',
        userVerification: 'required',
      },
      timeout: 60000,
      attestation: 'none',
    },
  }) as PublicKeyCredential | null;

  if (!credential) throw new Error('No se pudo registrar la biometria.');

  const stored: StoredBiometric = {
    credentialId: toBase64Url(credential.rawId),
    memberId: user.id,
    user,
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(BIOMETRIC_KEY, JSON.stringify(stored));
  return stored;
}

export async function authenticateWithBiometrics() {
  const stored = getStoredBiometric();
  if (!stored?.credentialId) throw new Error('Primero agrega datos biometricos en este dispositivo.');

  const available = await isBiometricAvailable();
  if (!available) throw new Error('La biometria no esta disponible en este dispositivo.');

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomChallenge(),
      allowCredentials: [
        {
          id: fromBase64Url(stored.credentialId),
          type: 'public-key',
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60000,
    },
  }) as PublicKeyCredential | null;

  if (!assertion) throw new Error('No se pudo confirmar la biometria.');
  return stored;
}
